import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Film, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";

const VFHistory = () => {
  const [videos, setVideos] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  const load = () =>
    api.get("/video-factory/history")
      .then(({ data }) => setVideos(data.videos || []))
      .catch(() => {});

  useEffect(() => { load(); }, []);

  const handleDelete = async (e, v) => {
    e.preventDefault();
    e.stopPropagation();
    const label = v.title || v.topic || "this video";
    if (!confirm(`Delete "${label}"? This permanently removes the draft, voiceover, and rendered files.`)) return;
    setDeletingId(v.id);
    try {
      await api.delete(`/video-factory/${v.id}`);
      setVideos((prev) => prev.filter((x) => x.id !== v.id));
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to delete video");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <PageContainer>
      <PageHeader title="Video History" subtitle="Re-edit, re-export, and republish anytime." testId="vf-history-header" />
      {videos.length === 0 ? (
        <div className="op-card"><EmptyState icon={Film} title="No videos in history" description="Once you generate videos, they'll appear here." action={<Link to="/video-factory/new" className="op-btn-primary">Create Video →</Link>} /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {videos.map((v) => (
            <div key={v.id} className="op-card overflow-hidden hover:border-[#7C3AED] relative group" data-testid={`hist-${v.id}`}>
              <Link to={`/video-factory/new/${v.id}`} className="block">
                <div className="aspect-video bg-gray-100 relative">
                  {v.thumbnails?.[0] ? <img src={v.thumbnails[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-300"><Film size={32} /></div>}
                  <span className={`absolute top-2 right-2 px-2 py-0.5 text-xs font-semibold rounded-full ${v.status === 'ready' ? 'bg-emerald-500 text-white' : v.status === 'published' ? 'bg-[#7C3AED] text-white' : 'bg-gray-200 text-gray-700'}`}>{v.status}</span>
                </div>
                <div className="p-3 pr-10">
                  <p className="text-sm font-medium text-gray-900 truncate">{v.title || v.topic}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{(v.platforms || []).slice(0, 3).join(", ")}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{new Date(v.created_at).toLocaleDateString()}</p>
                </div>
              </Link>
              <button
                type="button"
                onClick={(e) => handleDelete(e, v)}
                disabled={deletingId === v.id}
                title="Delete video"
                aria-label="Delete video"
                className="absolute bottom-2 right-2 p-1.5 rounded-md bg-white/90 backdrop-blur border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-300 disabled:opacity-50 transition"
                data-testid={`hist-delete-${v.id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </PageContainer>
  );
};
export default VFHistory;
